import React, { useContext, useState } from 'react';
import { Evaluation } from '../types/Scorecard';
import { EnrichedRegistrant } from '../types/Registrant';
import { EventContext } from '../context/EventContext';
import ToggleSwitch from './ToggleSwitch';
import { useToast } from '../context/ToastContext';

interface ExportRegistrantsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  registrants: EnrichedRegistrant[];
}

const ExportRegistrantsPopup: React.FC<ExportRegistrantsPopupProps> = ({ isOpen, onClose, registrants }) => {
  const [hideNotInvited, setHideNotInvited] = useState<boolean>(false);
  const [hideNotAttending, setHideNotAttending] = useState<boolean>(false);
  const [hideNotCheckedIn, setHideNotCheckedIn] = useState<boolean>(false);
  const { currentEvent } = useContext(EventContext);
  const [disableDownload, setDisableDownload] = useState<boolean>(false);
  const toast = useToast();
  const [allOn, setAllOn] = useState<boolean>(true);

  const baseKeys = [
    'first_name',
    'last_name',
    'email',
    'phone_number',
    'is_invited',
    // 'is_attending',
    'is_checked_in',
    'paid',
    ...(currentEvent?.event_type_id === 'MUSIC' ? ['ranking'] : []),
    'chair_number',
    'group_name',
    ...(!!currentEvent?.require_organization
      ? [
        'organization_name',
        'organization_bookkeeper_name',
        'organization_bookkeeper_phone',
        'organization_city',
        'organization_state',
        'city',
        'state',
        'organization_contact_email',
        'organization_emergency_contact',
        'organization_emergency_phone',
      ]
      : [])
  ];

  const dynamicKeys = new Set<string>();
  registrants.forEach((reg) => {
    reg.answers?.forEach((ans) => {
      if (ans.question_text && ans.type !== 'file') {
        dynamicKeys.add(ans.question_text);
      }
    });
  });

  const customDynamicKeys = new Set<string>();
  registrants.forEach((reg) => {
    reg.custom_answers?.forEach((ans) => {
      if (ans.question_text) {
        customDynamicKeys.add(ans.question_text);
      }
    });
  });

  const allExportKeys = [...baseKeys, ...Array.from(customDynamicKeys), ...Array.from(dynamicKeys)];
  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>(
    Object.fromEntries(allExportKeys.map((key) => [key, true]))
  );

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleAllSelected = () => {
    const newAllOn = !allOn;

    const newSelected = Object.fromEntries(
      allExportKeys.map((key) => [key, newAllOn])
    );

    setSelectedKeys(newSelected);
    setAllOn(newAllOn);
  };

  const downloadData = () => {
    setDisableDownload(true);
    // Step 1: Get selected keys
    const keysToExport = Object.entries(selectedKeys)
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => key);


    // Step 2: Create formatted headers
    const headers = keysToExport.map(key =>
      key
        .replaceAll('_', ' ')
        .replace(/\b[a-z]/g, (match, offset, str) => {
          // Check if the character before this match is an apostrophe
          const index = str.indexOf(match, offset);
          if (index > 0 && str[index - 1] === "'") {
            return match; // don't capitalize if preceded by apostrophe
          }
          return match.toUpperCase();
        })
    );

    // Step 3: Build data rows
    const rows = registrants
      .filter(reg => {
        if (hideNotInvited && !reg.is_invited) return false;
        // if (hideNotAttending && !reg.is_attending) return false;
        if (hideNotCheckedIn && !reg.is_checked_in) return false;
        return true;
      })
      .map(reg => {
        const answerMap: Record<string, string> = {};
        reg.answers?.forEach(ans => {
          if (ans.type !== 'file' && ans.question_text && ans.answer != null) {
            let value = '';

            if (
              ans.type === 'instrument' &&
              typeof ans.answer === 'object' &&
              'name' in ans.answer &&
              typeof ans.answer.name === 'string'
            ) {
              value = ans.answer.name;
            } else {
              value = ans.answer.toString();
            }

            answerMap[ans.question_text] = value;
          }
        });

        reg.custom_answers?.forEach(ans => {
          if (ans.question_text && ans.answer_text != null) {
            answerMap[ans.question_text] = ans.answer_text.toString();
          }
        });

        return keysToExport.map(key => {
          if (key in reg) {
            const value = (reg as any)[key];

            // Convert boolean to "Yes"/"No" for specific keys
            if (['is_attending', 'is_invited', 'is_checked_in', 'paid'].includes(key)) {
              return value ? 'Yes' : 'No';
            }

            return value ?? '';
          }
          if (key in answerMap) return answerMap[key];
          return '';
        });
      });

    // Step 4: Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${val}"`).join(','))
    ].join('\n');

    if (rows.length > 0) {
      toast.addToast('Download in progress, please wait.')
      // Step 5: Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CyneUp_registrants_export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.addToast('No data to export. Please check export options at the top of this popup.', true)
    }
    setDisableDownload(false);
  };


  if (!isOpen) return null;

  return (
    <div
      className="popOverBackdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="popOverContainer"
        style={{
          position: 'relative',
          background: 'white',
          borderRadius: '8px',
          padding: '20px',
          width: '80%',
          maxWidth: '800px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the popover
      >
        <h2>Export Registrants</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {!!currentEvent?.has_invitation && <ToggleSwitch
            label="Show Invited"
            checked={!hideNotInvited}
            onChange={() => { setHideNotInvited(!hideNotInvited) }}
          />}
          {/* {!!currentEvent?.has_invitation && <ToggleSwitch
            label="Show Attending"
            checked={!hideNotAttending}
            onChange={() => { setHideNotAttending(!hideNotAttending) }}
          />} */}
          {!!currentEvent?.has_checkin && <ToggleSwitch
            label="Show Checked-In"
            checked={!hideNotCheckedIn}
            onChange={() => { setHideNotCheckedIn(!hideNotCheckedIn) }}
          />}
          <ToggleSwitch
            label={`Select All`}
            checked={allOn}
            onChange={() => { toggleAllSelected() }}
          />
        </div>
        <h3 style={{ marginTop: '20px' }}>Export Fields</h3>
        <div className='tableWrapper' style={{ maxHeight: '75vh' }}>
          <table style={{ width: '90%', marginLeft: '5%' }}>
            <thead>
              <tr>
                <th>Field</th>
                <th>Include in Export</th>
              </tr>
            </thead>
            <tbody>
              {allExportKeys.map((key) => (
                <tr key={key}>
                  <td>{key
                    .replaceAll('_', ' ')
                    .replace(/\b[a-z]/g, (match, offset, str) => {
                      // Check if the character before this match is an apostrophe
                      const index = str.indexOf(match, offset);
                      if (index > 0 && str[index - 1] === "'") {
                        return match; // don't capitalize if preceded by apostrophe
                      }
                      return match.toUpperCase();
                    })}</td>
                  <td>
                    <ToggleSwitch
                      label={selectedKeys[key] ? 'Included' : 'Excluded'}
                      checked={selectedKeys[key]}
                      onChange={() => toggleKey(key)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>




        <div className="buttonsRow" style={{ marginTop: '20px', textAlign: 'right' }}>
          <button
            style={{
              fontSize: '16px',
              backgroundColor: '#888888',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            Close
          </button>
          <div className='buttonsRight'></div>
          <button onClick={downloadData} disabled={disableDownload} aria-label='Download Data'>Download Data</button>

        </div>
      </div>
    </div>
  );
};

export default ExportRegistrantsPopup;
